import type { ChartGranularity } from '@skystone/shared';

// ---------------------------------------------------------------------------
// Management Dashboard — Extended Date Presets
// Rolling windows (2w/4w/8w), quarters (Q1–Q4), YTD, Full Year + year selector
// ---------------------------------------------------------------------------

export type ManagementRollingPreset = '2w' | '4w' | '8w';
export type ManagementQuarterPreset = 'Q1' | 'Q2' | 'Q3' | 'Q4';
export type ManagementPeriodPreset = 'YTD' | 'full-year';
export type ManagementDatePreset =
  | ManagementRollingPreset
  | ManagementQuarterPreset
  | ManagementPeriodPreset;

export const ROLLING_PRESETS: ReadonlyArray<{ value: ManagementRollingPreset; label: string }> = [
  { value: '2w', label: '2w' },
  { value: '4w', label: '4w' },
  { value: '8w', label: '8w' },
];

export const QUARTER_PRESETS: ReadonlyArray<{ value: ManagementQuarterPreset; label: string }> = [
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Q2' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Q4', label: 'Q4' },
];

export const PERIOD_PRESETS: ReadonlyArray<{ value: ManagementPeriodPreset; label: string }> = [
  { value: 'YTD', label: 'YTD' },
];

const VALID_PRESETS = new Set<ManagementDatePreset>([
  '2w', '4w', '8w',
  'Q1', 'Q2', 'Q3', 'Q4',
  'YTD', 'full-year',
]);

const ROLLING_SET = new Set<string>(['2w', '4w', '8w']);

const ROLLING_DAYS: Record<ManagementRollingPreset, number> = {
  '2w': 13,
  '4w': 27,
  '8w': 55,
};

const QUARTER_BOUNDARIES: Record<ManagementQuarterPreset, { startMonth: number; endMonth: number; endDay: number }> = {
  Q1: { startMonth: 1, endMonth: 3, endDay: 31 },
  Q2: { startMonth: 4, endMonth: 6, endDay: 30 },
  Q3: { startMonth: 7, endMonth: 9, endDay: 30 },
  Q4: { startMonth: 10, endMonth: 12, endDay: 31 },
};

// --- Helpers ----------------------------------------------------------------

function getTodayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function getDaysAgoIso(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// --- Public API -------------------------------------------------------------

export function isRollingPreset(preset: ManagementDatePreset): preset is ManagementRollingPreset {
  return ROLLING_SET.has(preset);
}

export function isQuarterPreset(preset: ManagementDatePreset): preset is ManagementQuarterPreset {
  return preset === 'Q1' || preset === 'Q2' || preset === 'Q3' || preset === 'Q4';
}

export function getManagementPresetRange(
  preset: ManagementDatePreset,
  year: number,
): { from: string; to: string } {
  if (isRollingPreset(preset)) {
    return { from: getDaysAgoIso(ROLLING_DAYS[preset]), to: getTodayIso() };
  }

  if (isQuarterPreset(preset)) {
    const q = QUARTER_BOUNDARIES[preset];
    return {
      from: `${year}-${pad(q.startMonth)}-01`,
      to: `${year}-${pad(q.endMonth)}-${pad(q.endDay)}`,
    };
  }

  if (preset === 'YTD') {
    const today = getTodayIso();
    const currentYear = new Date().getUTCFullYear();
    const to = year < currentYear ? `${year}-12-31` : today;
    return { from: `${year}-01-01`, to };
  }

  // full-year
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export function getGranularity(preset: ManagementDatePreset): ChartGranularity {
  if (preset === '2w' || preset === '4w') return 'daily';
  if (preset === 'full-year') return 'monthly';
  // 8w, Q1–Q4, YTD
  return 'weekly';
}

export function getDefaultManagementPreset(): { preset: ManagementQuarterPreset; year: number } {
  const currentYear = new Date().getUTCFullYear();
  return { preset: 'Q1', year: currentYear };
}

export function getAvailableYears(earliestYear: number): number[] {
  const currentYear = new Date().getUTCFullYear();
  const years: number[] = [];
  for (let y = earliestYear; y <= currentYear; y++) {
    years.push(y);
  }
  return years;
}

/**
 * Parse the `preset` URL param.
 *
 * The optional `yearParam` controls how `'full-year'` is resolved (PRODOPSAI-370):
 * - `'full-year'` is still produced internally by the year-row buttons as
 *   `?preset=full-year&year=YYYY`. To keep the original Full Year semantics
 *   (Jan 1–Dec 31 of YYYY, monthly granularity) we keep `'full-year'` only when
 *   `yearParam` is an explicit past year inside the year-row range (currentYear
 *   minus one).
 * - Any other case — missing year, current year, or stale/out-of-range year —
 *   falls back to `'YTD'` (AC1.1.3). This avoids charts extending into the
 *   future when users click the current-year button and gives stale saved
 *   links a safe landing.
 */
export function parseManagementPresetParam(
  value: string | null,
  yearParam: string | null = null,
): ManagementDatePreset {
  if (value === 'full-year') {
    if (yearParam !== null) {
      const year = parseYearParam(yearParam);
      const currentYear = new Date().getUTCFullYear();
      const earliestSupported = currentYear - 1;
      if (year >= earliestSupported && year < currentYear) {
        return 'full-year';
      }
    }
    return 'YTD';
  }
  if (value && VALID_PRESETS.has(value as ManagementDatePreset)) {
    return value as ManagementDatePreset;
  }
  return getDefaultManagementPreset().preset;
}

export function parseYearParam(value: string | null): number {
  if (value) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed >= 2000 && parsed <= 2099) {
      return parsed;
    }
  }
  return getDefaultManagementPreset().year;
}
